const baseUrl = () => import.meta.env.VITE_API_URL || '';

/** Base da API configurada em `VITE_API_URL` (vazia ou relativa só com `/`). */
export function apiBaseUrl() {
  return baseUrl().trim();
}

/**
 * Converte `/api/uploads/...` gravado pela API URL absoluta quando `VITE_API_URL` é http(s).
 * Mantém só o caminho quando a base é relativa (proxy Vite mesmo host).
 * JPEG Base64 cru começa por `/9j/` — não é path; vira data URI para o `<img>` não quebrar.
 */
export function resolveApiMediaUrl(pathOrUrl) {
  const s = typeof pathOrUrl === 'string' ? pathOrUrl.trim() : '';
  if (!s) return '';
  if (s.startsWith('data:') || /^https?:\/\//i.test(s)) return s;

  // JPEG Base64 cru: sempre começa por `/9j/` (assinatura FF D8 FF)
  if (s.startsWith('/9j/')) {
    return `data:image/jpeg;base64,${s}`;
  }

  // Outros Base64 crus (PNG/GIF/WebP) — sem path e com tamanho mínimo
  if (!s.startsWith('/') && /^[A-Za-z0-9+/_=-]{64,}$/.test(s)) {
    const mime = s.startsWith('iVBOR')
      ? 'image/png'
      : s.startsWith('R0lGOD')
        ? 'image/gif'
        : s.startsWith('UklGR')
          ? 'image/webp'
          : 'image/jpeg';
    return `data:${mime};base64,${s}`;
  }

  const base = apiBaseUrl();
  if (!s.startsWith('/') || !base.startsWith('http')) {
    return s;
  }

  try {
    const root = base.endsWith('/') ? base : `${base}/`;
    return new URL(s, root).href;
  } catch {
    return s;
  }
}

/** Resposta da API JSON nunca começa com `<` após trim — se falhar o parse e isto for verdadeiro, veio página HTML (ex.: index.html da SPA). */
function responseLooksHtml(text) {
  const t = text.trimStart();
  return t.length > 0 && t.charAt(0) === '<';
}

/** @returns {{ parsed: unknown, plain: string }} */
function parseJsonBody(text) {
  const trimmed = text.trim();
  if (trimmed === '') {
    return { parsed: null, plain: trimmed };
  }
  try {
    return { parsed: JSON.parse(text), plain: trimmed };
  } catch {
    return { parsed: undefined, plain: trimmed };
  }
}

function getMsgHtmlInsteadOfApi() {
  return (
    'O servidor devolveu HTML em vez de JSON da API. ' +
    'Provavelmente não existe `/api` nesse domínio (o site estático volta a enviar index.html — comum em SPA em produção). ' +
    `Defina antes do build na raiz do projeto um VITE_API_URL absoluto até à pasta onde corre o router.php PHP (ex.: https://seudominio.com/subpastaDaApi ou outro dominio próprio para a API — sem "/" no final) e rode de novo npm run build. Actual: "${baseUrl() || '(vazio → mesmo dominio)'}"`
  );
}

export function setJwt(token) {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem('ominia.jwt', token);
  }
}

export function clearJwt() {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem('ominia.jwt');
  }
}

function getToken() {
  return typeof window !== 'undefined' ? window.sessionStorage.getItem('ominia.jwt') : null;
}

export async function api(path, opts = {}) {
  const headers = new Headers(opts.headers || {});
  if (!headers.has('Content-Type') && opts.body && !(opts.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const t = opts.authToken ?? getToken();
  if (t && !opts.skipAuth) headers.set('Authorization', `Bearer ${t}`);

  const method = String(opts.method || 'GET').toUpperCase();
  let res;
  try {
    res = await fetch(`${baseUrl()}${path}`, {
      ...opts,
      headers,
      cache: method === 'GET' ? 'no-store' : opts.cache ?? 'default',
    });
  } catch (e) {
    const isNetwork =
      e instanceof TypeError ||
      (typeof e?.message === 'string' && /failed to fetch|networkerror|load failed/i.test(e.message));
    if (isNetwork) {
      const base = baseUrl() || '(vazio → mesmo host)';
      throw Object.assign(
        new Error(
          'Falha de rede ou bloqueio CORS. No servidor (pasta api/.env): defina `CORS_ORIGIN` com o URL EXACTO ' +
            'do site que abriu — inclua https se usa SSL (ex.: `https://pedido.ominia.app` e não só http). ' +
            'Se trabalha em `npm run dev` contra API remota, inclua também `http://localhost:5173`. ' +
            'Em dev local com proxy: `VITE_API_URL=/api` e `php -S 127.0.0.1:4000 router.php` dentro de `api`. ' +
            `Base actual da API: ${base}`,
        ),
        { cause: e },
      );
    }
    throw e;
  }
  const text = await res.text();
  const { parsed: data, plain: trimmedBody } = parseJsonBody(text);

  const htmlLike = trimmedBody !== '' && responseLooksHtml(trimmedBody);

  if (!res.ok) {
    let errMsg =
      typeof data === 'object' && data && data !== null && 'error' in data
        ? String(data.error)
        : htmlLike || (data === undefined && trimmedBody !== '')
          ? htmlLike
            ? getMsgHtmlInsteadOfApi()
            : trimmedBody.slice(0, 240)
          : res.statusText;
    if (
      typeof data === 'object' &&
      data &&
      data !== null &&
      'detail' in data &&
      data.detail != null &&
      String(data.detail).trim()
    ) {
      errMsg += ` — ${data.detail}`;
    }
    if (
      typeof data === 'object' &&
      data &&
      data !== null &&
      'hint' in data &&
      data.hint != null &&
      String(data.hint).trim()
    ) {
      errMsg += ` ${String(data.hint).trim()}`;
    }
    if (
      res.status === 402 &&
      typeof data === 'object' &&
      data &&
      data !== null &&
      data.code === 'TENANT_SUBSCRIPTION_OVERDUE' &&
      typeof window !== 'undefined'
    ) {
      window.dispatchEvent(
        new CustomEvent('ominia:subscription-overdue', {
          detail: { error: typeof data.error === 'string' ? data.error : '' },
        }),
      );
    }
    throw Object.assign(new Error(errMsg), { status: res.status, data: data ?? text });
  }

  // Sucesso 200 mas corpo não é JSON → comum quando /api não existe e o .htaccess devolve index.html (catálogo ficava vazio).
  if (trimmedBody !== '' && data === undefined) {
    throw Object.assign(
      new Error(responseLooksHtml(trimmedBody) ? getMsgHtmlInsteadOfApi() : 'Resposta da API não é JSON válido.'),
      { status: res.status },
    );
  }

  return data;
}
